import { useStacklineTheme } from '@stackline/ui';
import React, { useMemo, useState } from 'react';
import {
  AppFilterGroupProps,
  MAX_OPTIONS_SHOWN
} from 'src/components/BeaconV2Components/common/AppFilterButton/AppFilterModal';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { FormControlLabel, Checkbox, Radio } from '@mui/material';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SelectedIncludeCheckbox } from 'src/components/SvgIcons';
import { FilterSearchInput } from 'src/components/BeaconV2Components/ProductGrowthModal/ModalComponents/FilterSearchInput';
import { useAppSelector } from 'src/utils/Hooks';
import StyledCheckbox from 'src/components/BeaconV2Components/common/StyledCheckbox';

interface BusinessUnitFilterGroupProps extends AppFilterGroupProps {
  selectedBusinessUnitFieldOption: Set<string>;
}

const BusinessUnitFilterGroup = ({
  filterTitle,
  options,
  onSelect,
  isRadio = false,
  placeholderText = '',
  hideSearchInput = false,
  selectedBusinessUnitFieldOption
}: BusinessUnitFilterGroupProps) => {
  // Pagination for "Show more" button
  const [optionsPage, setOptionsPage] = useState(1);
  // Input used for searching categories or subcategories
  const [input, setInput] = useState('');
  const theme = useStacklineTheme();
  const segments = useAppSelector((state) => state.segments);
  const { savedSearchesById } = segments;

  const userInput = useMemo(() => {
    return input;
  }, [input]);

  /**
   * If the user types in the search bar, we should search all available options.
   * Otherwise we should only show subcategories under a selected parent category.
   * If no category is selected, we can make all options available.
   * Note that we limit (paginate) available options unless the user is searching.
   */
  const availableOptions = useMemo(() => {
    if (userInput.length > 0) {
      return options
        .filter((option) => option.id !== 0 && option.displayName.toLowerCase().includes(userInput.toLowerCase()))
        .map((option) => {
          return {
            id: option.id,
            label: option.displayName,
            isSelected: option.isSelected
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    } else {
      return options
        .slice(0, optionsPage * MAX_OPTIONS_SHOWN)
        .filter((cat) => cat.id && cat.id !== 0)
        .map((option) => {
          return {
            id: option.id,
            label: option.displayName,
            isSelected: option.isSelected
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }, [optionsPage, userInput, options]);

  const showMoreOptions = () => {
    setOptionsPage(optionsPage + 1);
  };

  const showLessOptions = () => {
    setOptionsPage(1);
  };

  // Check if we've shown all options
  const totalOptions = options.filter((opt: any) => opt.id && opt.id !== 0).length;
  const hasMoreOptions = totalOptions > optionsPage * MAX_OPTIONS_SHOWN;
  const isExpanded = optionsPage > 1;
  const shouldShowButton = totalOptions > MAX_OPTIONS_SHOWN;

  return (
    <>
      <Flex flexDirection="column">
        {/* FilterTitle */}
        <Flex marginTop={theme.spacing.mdl}>
          <Text variant="subtitle2">{filterTitle}</Text>
        </Flex>

        {/* Search Input  */}
        {!hideSearchInput && (
          <Flex marginTop={theme.spacing.md} marginBottom="10px">
            <FilterSearchInput handleChange={setInput} input={input} placeholderText={placeholderText} />
          </Flex>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, auto)',
            color: theme.colors.primary,
            justifyContent: 'space-between',
            rowGap: '16px',
            marginTop: '10px',
            marginBottom: theme.spacing.sm
          }}
        >
          <>
            {availableOptions ? (
              <>
                {availableOptions.map((option) => (
                  <FormControlLabel
                    title={option.label}
                    sx={{
                      gap: '8px',
                      color: theme.colors.primary,
                      minWidth: '190px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginLeft: '0px',
                      marginRight: '0px',
                      '& .MuiTypography-root': {
                        width: '160px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }
                    }}
                    checked={option.isSelected}
                    label={option.label}
                    value={option.id}
                    control={
                      isRadio ? (
                        <Radio
                          disableRipple
                          disableTouchRipple
                          sx={{
                            padding: '0',
                            svg: {
                              height: '15px',
                              width: '15px'
                            },
                            '&, &.Mui-checked': {
                              color: theme.colors.primary
                            }
                          }}
                        />
                      ) : (
                        <StyledCheckbox
                          iconProps={{
                            width: '14px',
                            height: '14px'
                          }}
                        />
                      )
                    }
                    key={option.id}
                    onChange={(e) => onSelect(e)}
                  />
                ))}
                {/* Prevent justify content with small number of items */}
                {availableOptions.length < 3 &&
                  Array.from({ length: 3 - availableOptions.length }).map((_, index) => (
                    <div key={`placeholder-${index}`} style={{ width: '192px', display: 'hidden' }}></div>
                  ))}
              </>
            ) : null}
          </>
        </div>

        {/* Show more/less button */}

        {!hideSearchInput && shouldShowButton ? (
          <button
            style={{
              paddingLeft: 0,
              border: 'none',
              background: 'none',
              textAlign: 'left',
              marginTop: theme.spacing.mdl,
              marginBottom: theme.spacing.sm
            }}
            onClick={isExpanded && !hasMoreOptions ? showLessOptions : showMoreOptions}
          >
            <Text variant="subtitle2" underlined>
              {isExpanded && !hasMoreOptions ? 'Show less' : 'Show more'}
            </Text>
          </button>
        ) : null}
      </Flex>

      {availableOptions &&
        availableOptions.length > 0 &&
        availableOptions
          .filter((availableOption) => {
            const savedSearchAvailableOption = savedSearchesById.get(availableOption.id);
            return availableOption.isSelected && savedSearchAvailableOption.type === 'businessunit';
          })
          .map((filteredOption) => {
            const filteredOptionObject = savedSearchesById.get(filteredOption.id);
            const nestedChildOptions = filteredOptionObject.children?.map((child) => {
              const nestedChildOption = savedSearchesById.get(child);

              return { ...nestedChildOption, isSelected: selectedBusinessUnitFieldOption.has(child) };
            });
            return (
              <BusinessUnitFilterGroup
                key={filteredOption.id}
                filterTitle={filteredOption.label}
                options={nestedChildOptions}
                isRadio={false}
                onSelect={onSelect}
                placeholderText="Search business units"
                selectedBusinessUnitFieldOption={selectedBusinessUnitFieldOption}
              />
            );
          })}
    </>
  );
};

export default BusinessUnitFilterGroup;
